import React, { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";

/**
 * Rotates through `words` with a per-letter staggered reveal.
 * Elegant cubic-bezier easing, no abrupt jump.
 */
export default function RotatingWord({
  words = [],
  interval = 2800,
  className = "",
  stagger = 0.04,
  duration = 0.7,
}) {
  const [i, setI] = useState(0);
  useEffect(() => {
    if (words.length < 2) return;
    const t = setInterval(() => setI((v) => (v + 1) % words.length), interval);
    return () => clearInterval(t);
  }, [words.length, interval]);

  const current = words[i] || "";
  const longest = words.reduce((a, b) => (a.length > b.length ? a : b), "");
  const letters = current.split("");

  return (
    <span className={`relative inline-block align-baseline ${className}`} data-testid="rotating-word">
      {/* Reserve space using the longest variant */}
      <span aria-hidden className="invisible whitespace-pre">{longest}</span>

      <AnimatePresence mode="wait">
        <motion.span
          key={current}
          aria-label={current}
          className="absolute left-0 top-0 whitespace-pre"
          initial="enter"
          animate="visible"
          exit="exit"
          variants={{
            enter: { transition: { staggerChildren: stagger, delayChildren: 0 } },
            visible: { transition: { staggerChildren: stagger } },
            exit: { transition: { staggerChildren: stagger * 0.6, staggerDirection: -1 } },
          }}
        >
          {letters.map((ch, idx) => (
            <motion.span
              key={`${current}-${idx}`}
              className="inline-block text-gradient-pink"
              style={{ willChange: "transform, opacity, filter" }}
              variants={{
                enter:   { opacity: 0, y: "0.35em", filter: "blur(10px)" },
                visible: { opacity: 1, y: 0, filter: "blur(0px)", transition: { duration, ease: [0.22, 1, 0.36, 1] } },
                exit:    { opacity: 0, y: "-0.35em", filter: "blur(10px)", transition: { duration: duration * 0.6, ease: [0.55, 0, 0.7, 0.2] } },
              }}
            >
              {ch === " " ? "\u00A0" : ch}
            </motion.span>
          ))}
        </motion.span>
      </AnimatePresence>
    </span>
  );
}
